export type ButtonChooserItem = { key: string; description: string }

export type ButtonChooserCloseEvent =
  | { kind: 'choose'; key: string }
  | { kind: 'dismiss' }

/** Swing `showButtonChooser(Object[][])` delegates here. */
export const SWING_BUTTON_CHOOSER_COLUMNS = 3

function unwrapChooserValue(value: unknown): unknown {
  if (
    value &&
    typeof value === 'object' &&
    'type' in value &&
    'value' in value
  ) {
    return (value as { value: unknown }).value
  }
  return value
}

function asChooserText(raw: unknown): string {
  const value = unwrapChooserValue(raw)
  if (value == null) return ''
  return String(value)
}

/**
 * Swing variants are `[[key, label], ...]` or, on the wire, `MapEntry { name, value }`.
 */
export function mapButtonChooserItems(variants: unknown): ButtonChooserItem[] {
  if (!Array.isArray(variants)) return []
  return variants.map((variant) => {
    if (Array.isArray(variant)) {
      return {
        key: asChooserText(variant[0]),
        description: asChooserText(variant[1] ?? variant[0]),
      }
    }
    if (variant && typeof variant === 'object') {
      const record = variant as Record<string, unknown>
      const key = asChooserText(record.name ?? record.key ?? record.id)
      return {
        key,
        description: asChooserText(
          record.value ?? record.description ?? record.label ?? key,
        ),
      }
    }
    const key = asChooserText(variant)
    return { key, description: key }
  })
}

/** @deprecated Use `mapButtonChooserItems`. Kept for existing call sites. */
export function mapButtonChooserArrayItems(variants: unknown): ButtonChooserItem[] {
  return mapButtonChooserItems(variants)
}

/**
 * Swing places a lone button on an incomplete last row in column 1
 * (`x == 0 ? 1 : x`), which centers it when there are 3 columns.
 * Returns a 0-based column index.
 */
export function buttonChooserCellColumn(index: number, count: number, columns: number): number {
  const perRow = Math.max(1, Math.floor(Number(columns)) || 1)
  const x = index % perRow
  const last = index === count - 1
  const incomplete = count % perRow !== 0
  if (last && incomplete && x === 0 && perRow > 1) return 1
  return x
}

/**
 * Swing: click a variant button → that key; close via X → null.
 * No OK/Cancel footer (unlike `showChooser`).
 */
export function resolveButtonChooserChoice(event: ButtonChooserCloseEvent): string | null {
  return event.kind === 'choose' ? event.key : null
}
